import { ArrowUpIcon } from "@heroicons/react/24/outline";
import { useI18N } from "@sundae/react-hooks";
import {
  DiscordLogoIcon,
  FadeInView,
  GitHubLogoIcon,
  Text,
  TwitterXLogoIcon,
  toolkitCx,
} from "@sundae/ui-toolkit";
import { ITextProps } from "@sundae/ui-toolkit/dist/types/components/atoms/Text/Text";
import cx from "classnames";
import { FC, HTMLAttributes, memo, useMemo } from "react";
import { TARGET_BLANK } from "../../constants/href.constants";
import { externalRoutes } from "../../pages/routes";

interface IFooterProps extends HTMLAttributes<HTMLDivElement> {
  size?: ITextProps["size"];
}

const Footer: FC<IFooterProps> = ({ className, size = "xl", ...rest }) => {
  const { t } = useI18N();

  const iconSize = useMemo(() => {
    switch (size) {
      case "xl":
        return "h-8 w-8";
      case "lg":
        return "h-7 w-7";
      case "md":
      case "base":
        return "h-6 w-6";
      case "sm":
        return "h-5 w-5";
      case "xs":
        return "h-4 w-4";
      default:
        return "h-8 w-8";
    }
  }, [size]);

  const backToTopSize = useMemo(() => {
    switch (size) {
      case "xl":
        return "h-14 w-14";
      case "lg":
        return "h-12 w-12";
      case "md":
      case "base":
        return "h-10 w-10";
      case "sm":
        return "h-8 w-8";
      case "xs":
        return "h-6 w-6";
      default:
        return "h-14 w-14";
    }
  }, [size]);

  const iconCx = cx(
    "hover:scale-110",
    iconSize,
    toolkitCx.layout.text.default,
    toolkitCx.recipes.scaleWhenActive,
    toolkitCx.layout.transition.default
  );

  return (
    <footer
      {...rest}
      className={cx("relative bottom-8 z-0 flex w-full items-end justify-between", className)}
    >
      <div className="flex flex-col gap-12">
        <div className="flex flex-col gap-2">
          <Text tag="h2" size={size}>
            © {new Date().getFullYear()} Sundae Labs, Inc.
          </Text>
          <Text tag="h2" size={size} variant="silent">
            {t("allRightsReserved")}.
          </Text>
        </div>

        <div className="flex items-center gap-6">
          <a href={externalRoutes.github} {...TARGET_BLANK}>
            <GitHubLogoIcon
              className={cx(
                iconCx,
                "hover:!text-[#6e5494]" // hover color taken from `socialIconColorMap`
              )}
            />
          </a>
          <a href={externalRoutes.twitter} {...TARGET_BLANK}>
            <TwitterXLogoIcon
              className={cx(
                iconCx,
                "hover:!text-[#1da1f2]" // hover color taken from `socialIconColorMap`
              )}
            />
          </a>
          <a href={externalRoutes.discord} {...TARGET_BLANK}>
            <DiscordLogoIcon
              className={cx(
                iconCx,
                "hover:!text-[#5865f2]" // hover color taken from `socialIconColorMap`
              )}
            />
          </a>
        </div>
      </div>

      <FadeInView rootMargin="100px">
        <div
          className={cx(
            "flex h-fit w-fit cursor-pointer flex-col items-center gap-2 hover:-translate-y-2",
            toolkitCx.layout.transition.transform
          )}
          role="button"
          onClick={() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <div
            className={cx(
              "flex items-center justify-center rounded-full bg-primary/5",
              backToTopSize
            )}
          >
            <ArrowUpIcon className={cx("text-primary", iconSize)} />
          </div>
          <Text tag="span" size="xs" variant="muted">
            {t("back", { context: "toTop" })}
          </Text>
        </div>
      </FadeInView>
    </footer>
  );
};

export default memo(Footer);
